import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon, Plus, CalendarClock, ArrowRight, Users } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { money, byPatientId } from "@/lib/api";

const BINDER_LIMIT = 24;
const todayStr = () => new Date().toLocaleDateString("en-CA");

// One row in a grouped list. Rows share a panel instead of each being its own card.
function PatientRow({ p, s, showMoney, testid }) {
  return (
    <Link to={`/app/patients/${p.id}`} data-testid={testid}
      className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-slate-50">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--dm-accent-soft)] font-head text-xs font-bold dm-accent-text">
        {(p.name || "?").slice(0, 1).toUpperCase()}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-slate-800">{p.name}</span>
        <span className="block truncate text-xs text-slate-500">
          ID {p.code ?? "—"} · {p.gender || "—"}{p.age ? ` · ${p.age}` : ""} · {p.phone || "No phone"}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block text-[13px] font-medium text-slate-800">{s.visits} visit{s.visits === 1 ? "" : "s"}</span>
        <span className="block font-mono text-xs text-slate-500">{showMoney ? money(s.total) : "•••"}</span>
      </span>
    </Link>
  );
}

export default function Search() {
  const { data, showMoney } = useApp();
  const [q, setQ] = useState("");
  const t = q.trim().toLowerCase();

  const stats = (id) => {
    const v = data.visits.filter((x) => x.patient_id === id);
    return { visits: v.length, total: v.reduce((a, x) => a + Number(x.clinic_share || 0), 0), last: v.map((x) => x.date).sort().pop() };
  };

  const results = useMemo(() => {
    if (!t) return [];
    return data.patients
      .filter((p) =>
        (p.name || "").toLowerCase().includes(t) ||
        (p.phone || "").includes(t) ||
        String(p.code || "").includes(t))
      .slice(0, 40);
  }, [t, data.patients]);

  const today = todayStr();
  const todaysPatients = useMemo(
    () => data.patients.filter((p) => p.next_appointment === today)
      .sort((a, b) => (a.next_appointment_time || "").localeCompare(b.next_appointment_time || "")),
    [data.patients, today]
  );
  const binder = useMemo(
    () => [...data.patients].sort(byPatientId),
    [data.patients]
  );

  return (
    <div className="mx-auto max-w-4xl" data-testid="search-page">
      <div className="mb-4">
        <h1 className="dm-page-title">
          Patient <span className="font-extrabold">records</span>
        </h1>
      </div>

      <div className="relative">
        <SearchIcon size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="search-input" autoFocus className="dm-input !py-2.5 !pl-10 !text-[15px]"
          placeholder="Search by name, phone, or Patient ID…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {t ? (
        results.length === 0 ? (
          <div className="dm-empty mt-6" data-testid="search-no-results">No patients found</div>
        ) : (
          <ul className="dm-panel mt-4 divide-y divide-slate-100 overflow-hidden" data-testid="search-results">
            {results.map((p) => (
              <li key={p.id}><PatientRow p={p} s={stats(p.id)} showMoney={showMoney} testid={`search-result-${p.id}`} /></li>
            ))}
          </ul>
        )
      ) : data.patients.length === 0 ? (
        <div className="dm-empty mt-6 py-14" data-testid="search-empty">
          <Users size={28} className="mx-auto mb-3 text-slate-300" />
          No patients yet. Log your first visit to get started.
          <div className="mt-5">
            <Link to="/app/log-visit" className="dm-btn-primary" data-testid="search-add-visit"><Plus size={15} /> Log visit</Link>
          </div>
        </div>
      ) : (
        <div className="mt-5 space-y-5">
          {todaysPatients.length > 0 && (
            <div>
              <div className="mb-2 flex items-center justify-between gap-3 px-1">
                <p className="label-xs flex items-center gap-1.5"><CalendarClock size={13} /> Today's appointments</p>
                <Link to="/app/calendar" data-testid="search-open-calendar" className="dm-link inline-flex items-center gap-1 text-xs">Open calendar <ArrowRight size={12} /></Link>
              </div>
              <ul className="dm-panel divide-y divide-slate-100 overflow-hidden" data-testid="search-today">
                {todaysPatients.map((p) => (
                  <li key={p.id}><PatientRow p={p} s={stats(p.id)} showMoney={showMoney} testid={`search-today-${p.id}`} /></li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <p className="label-xs mb-2 px-1">All patients ({binder.length.toLocaleString("en-IN")})</p>
            {/* Hairlines come from the 1px grid gap, so every cell is divided evenly in one or two columns. */}
            <ul className="dm-panel grid grid-cols-1 gap-px overflow-hidden !bg-slate-100 lg:grid-cols-2" data-testid="search-binder">
              {binder.slice(0, BINDER_LIMIT).map((p) => (
                <li key={p.id} className="bg-white"><PatientRow p={p} s={stats(p.id)} showMoney={showMoney} testid={`search-binder-${p.id}`} /></li>
              ))}
            </ul>
            {binder.length > BINDER_LIMIT && (
              <Link to="/app/directory" data-testid="search-see-all"
                className="mt-2 flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-[13px] font-semibold text-slate-600 transition-colors hover:bg-white hover:text-slate-900">
                Showing {BINDER_LIMIT} of {binder.length.toLocaleString("en-IN")}. Open the full directory <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
